/**
 * AppShell — the two-column chrome every authenticated route lives inside.
 *
 *   ┌────────────┬────────────────────────────────────────────┐
 *   │            │  Topbar                                    │
 *   │  Sidebar   ├────────────────────────────────────────────┤
 *   │  (sticky)  │                                            │
 *   │            │  <main> scrollable content                 │
 *   │            │  px-4 sm:px-6 lg:px-8 py-6                 │
 *   └────────────┴────────────────────────────────────────────┘
 *
 * On desktop (>= lg) the sidebar is a persistent column whose width follows
 * a persisted collapse preference (localStorage). On mobile the sidebar
 * becomes a Sheet drawer opened from the topbar hamburger.
 */
import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useGlobalSettingsCacheSync } from '@/shared/api/global-settings';
import { OfflineBanner } from '@/shared/offline/OfflineBanner';
import { GlobalHotkeysProvider } from '@/shared/features/global-hotkeys';
import { Sheet, SheetContent, SheetTitle } from './sheet';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';
import { cn } from './utils';
import { OmeecronFooter } from './omeecron-brand';
import { useDocumentTitle } from './use-document-title';

/** LocalStorage key for the desktop sidebar collapsed preference. */
const SIDEBAR_COLLAPSED_KEY = 'erp_sidebar_collapsed';

export interface AppShellProps {
  children: ReactNode;
  /** Optional class merged onto the outer wrapper. */
  className?: string;
}

export function AppShell({ children, className }: AppShellProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsedPref());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Keeps the Global Settings module-level caches (default currency, default
  // country) warm for the whole authenticated session — see
  // shared/api/global-settings.ts. Every page under AppShell benefits
  // without adding its own query.
  useGlobalSettingsCacheSync();

  // The browser tab carries the client's company name, not the project's.
  useDocumentTitle();

  // Persist the collapse preference so it survives full reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      /* localStorage may be blocked (private mode, quota) — ignore. */
    }
  }, [collapsed]);

  // Any route change closes the mobile drawer. Consumers of AppShell can
  // rely on the topbar hamburger for re-opening it.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onPop = () => setMobileNavOpen(false);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [mobileNavOpen]);

  const toggleCollapsed = useCallback(() => setCollapsed((v) => !v), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);

  return (
    <GlobalHotkeysProvider>
    <div
      className={cn(
        'flex h-screen w-full bg-background text-foreground overflow-hidden',
        className,
      )}
    >
      {/* Desktop sidebar */}
      <div className="hidden shrink-0 lg:block">
        <Sidebar
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </div>

      {/* Mobile drawer sidebar. Tapping outside or the built-in X closes it. */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          // Override the Sheet default max-w-md with a wider mobile drawer.
          // We deliberately leave `w-full` from the variant so the drawer
          // still fills the screen up to max-w on narrow devices.
          className="max-w-[85vw] gap-0 border-r border-sidebar-border bg-sidebar p-0"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar inDrawer />
        </SheetContent>
      </Sheet>

      {/* Right column: topbar + scrolling main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onOpenMobileNav={openMobileNav} />
        <OfflineBanner />
        <main
          id="app-main"
          className="flex-1 overflow-y-auto"
          role="main"
          tabIndex={-1}
        >
          <div
            className={cn(
              'mx-auto w-full max-w-[1600px]',
              'p-4 md:p-5',
            )}
          >
            {children}
          </div>

          {/* Attribution sits inside the scrolling region, after the page
              content, so it never covers anything on a short mobile viewport
              the way a fixed footer would. */}
          <footer className="mx-auto w-full max-w-[1600px] px-4 pb-6 pt-2 md:px-5">
            <OmeecronFooter compact className="border-t border-border/60 pt-4" />
          </footer>
        </main>
      </div>
    </div>
    </GlobalHotkeysProvider>
  );
}

/**
 * Read the sidebar-collapsed pref from localStorage safely.
 * Falls back to `false` when storage is unavailable or the value is corrupt.
 */
function readCollapsedPref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}
